import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Button as UIButton } from 'reactstrap';
import Context from '../../containers/App/MainProvider/context';
import Table, { Cell } from './Table';
import useUploadFiles from '../hooks/useUploadFiles';
import Textarea from './Textarea';
import { LibraryBaseService } from '../../services/types/LibraryBase';
import Button from './Button';
import { SpinnerArea } from '.';

interface Doc {
  id: number;
  name: string;
  index: string;
}

interface Props {
  docService: LibraryBaseService;
  id: number;
  savedComment?: string;
  reload: () => void;
  addButtonBelow?: boolean;
}

const Library = ({
  docService,
  id,
  savedComment = '',
  reload: reloadModel,
  addButtonBelow,
}: Props) => {
  const { t } = useTranslation();

  const { service, notify } = useContext(Context);

  const [formData, setFormData, selectFiles, deleteFile] = useUploadFiles();
  const [comment, setComment] = useState(savedComment);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filesMap = formData
    ? Array.from(formData.entries()).map(
        (x, i) => ({ id: i, name: (x[1] as File).name, index: x[0] } as Doc)
      )
    : [];

  const cells: Cell<Doc>[] = [
    {
      name: 'name',
      column: 'documentName',
      value: (x) => x.name,
    },
    {
      column: 'delete',
      css: 'text-right',
      render: (_, obj) => (
        <Button
          className="btn-tertiary no-padding"
          icon="trash-alt"
          text=""
          onClick={() => deleteFile(obj.name)}
        />
      ),
    },
  ];

  const save = () => {
    if (comment) {
      const dataForm = new FormData();

      if (formData) {
        Array.from(formData.entries()).forEach((x) => dataForm.append(x[0], x[1]));
      }

      dataForm.append('comment', comment);
      service(() => docService.saveDocuments(id, dataForm), {
        callback: () => {
          setFormData(null);
          setComment('');
          reloadModel();
          notify(t('documents'), t('savedSuccessfully'), 'success');
        },
        loader: setIsSubmitting,
      });
    }
  };

  const saveComment = () => {
    if (comment) {
      service(() => docService.saveDocumentComment({ modelId: id, comment }), {
        callback: () => {
          notify(t('documents'), t('savedSuccessfully'), 'success');
        },
        loader: setIsSubmitting,
      });
    }
  };

  const addButton = (
    <UIButton
      className="py-1"
      type="button"
      color="primary"
      disabled={isSubmitting || !comment}
      onClick={save}
      itemID="btn_saveDocument"
    >
      {t('add')}
    </UIButton>
  );

  return (
    <SpinnerArea loading={isSubmitting}>
      <Card className="flow-card">
        <CardBody>
          <h5 className="mb-3 mt-n3">{t('library')}</h5>{' '}
          <div className="flow-feedback-content">
            <div className="flow-feedback-content__comment">
              <Textarea
                name={t('comment')}
                placeholder={t('typeYourComment')}
                maxLength={1000}
                onChange={(e) => setComment(e.target.value)}
                value={comment}
                label={t('addComment')}
                scrollable
              />
            </div>
            <div className="d-flex justify-content-between">
              <Button
                className="btn-secondary py-1"
                onClick={() => saveComment()}
                text={t('saveCommentBeforeAdd')}
              />
              {!addButtonBelow && addButton}
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <h5>Selected Documents</h5>
            <div className="upload-btn-wrapper">
              <Button
                htmlId="btn-mici-uploader"
                text={t('uploadNew')}
                icon="file-upload"
                className="btn-tertiary p-0"
              />
              <input placeholder="" type="file" onChange={selectFiles} multiple />
            </div>
          </div>
          {filesMap.length > 0 ? (
            <Table data={filesMap} cells={cells} keyFunc={(x) => x.id} />
          ) : (
            <div className="centered-big-text">{t('emptyAttachments')}</div>
          )}
          {addButtonBelow && <div className="d-flex justify-content-end p-3">{addButton}</div>}
        </CardBody>
      </Card>
    </SpinnerArea>
  );
};

export default Library;
